import * as React from 'react';
import Image from 'next/image';
import styled, { DefaultTheme, StyledComponent } from 'styled-components';
import { useInView } from 'react-intersection-observer';
import ButtonBase from '@mybet/components/Button';
import { gtmCategories, gtmEvents } from '@mybet/core/utils/trackGTM';
import RouterLink, { RouterLinkProps } from '@mybet/core/components/RouterLink';
import Typography from '@mybet/components/Typography';
import PaymentIcon, {
  PaymentMethodIcon,
} from '@mybet/core/components/PaymentIcon';
import ThirdPartyIcon, { ThirdPartyIconType } from './partials/ThirdPartyIcon';
import CertificateIcon, {
  CertificateIconType,
} from './partials/CertificateIcon';
import Section from '@mybet/core/components/Section';
import Flex from '@mybet/components/Flex';
import LanguageSelector, {
  LanguageSelectorProps,
} from '@mybet/core/components/LanguageSelector';
import useMatchMedia from '@mybet/core/utils/useMatchMedia';
import { Link } from '@mybet/core/libs/apollo/cache';

export type AppFooterProps = {
  certificateIcons: { icon: CertificateIconType; href?: string }[];
  thirdPartyIcons?: ThirdPartyIconType[];
  paymentIcons: PaymentMethodIcon[];
  LanguageSelectorProps?: LanguageSelectorProps;
  links: Link[];
  infoLinks: Link[];
  translations: Record<string, string>;
  Link?:
    | React.ElementType<RouterLinkProps | HTMLAnchorElement>
    // eslint-disable-next-line @typescript-eslint/ban-types
    | StyledComponent<'a', DefaultTheme, {}, never>;
  homeLink?: string;
};

const Root = styled.footer((props) => ({
  position: 'relative',
  backgroundColor: props.theme.grey02,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: props.theme.spacing(2, 0, 8),
  zIndex: 10,

  [props.theme.breakpoints.up('lg')]: {
    padding: props.theme.spacing(6, 0, 8),
  },
}));

const Button = styled(ButtonBase)({
  border: 'none',
});

const ChildrenRoot = styled(Flex)((props) => ({
  maxWidth: props.theme.breakpoints.width('lg'),
}));

const FooterSection = styled(Section)((props) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  width: '100%',

  '& button': {
    maxWidth: '90%',
    color: props.theme.blue13,
    fontWeight: 400,
    fontSize: 12,
    '&:hover': {
      backgroundColor: props.theme.white,
    },
  },
}));

const FooterContent = styled.div((props) => ({
  padding: props.theme.spacing(3, 2),
}));

const FooterEnd = styled.div((props) => ({
  padding: props.theme.spacing(4, 2),
  borderTop: `1px solid ${props.theme.grey03}`,

  [Typography]: {
    color: props.theme.grey08,
    fontSize: 11,
  },
}));

const PaymentIconRoot = styled.div((props) => ({
  height: 48,
  margin: '6px 3px 0',

  [props.theme.breakpoints.up('sm')]: {
    margin: '6px 4px 0',
  },
}));

const FooterLinkItem = styled(Typography)((props) => ({
  color: props.theme.blue13,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  margin: props.theme.spacing(0.75, 1.5),
  cursor: 'pointer',
  display: 'inline-block',
}));

const LanguageSelectorSection = styled.div({
  width: 220,
});

const AppFooter: React.FC<AppFooterProps> = (props) => {
  const {
    certificateIcons,
    thirdPartyIcons = [],
    paymentIcons,
    LanguageSelectorProps,
    links,
    infoLinks,
    translations: t,
    Link = RouterLink,
    homeLink = '/sport#home',
  } = props;
  const { matches } = useMatchMedia('md');
  const [certificateSectionRef, certificateSectionInView] = useInView();
  const [thirdPartySectionRef, thirdPartySectionInView] = useInView();

  const handleScrollTo = React.useCallback(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  return (
    <Root>
      <ChildrenRoot
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        {!matches && (
          <>
            <FooterSection gutterBottom={4}>
              <Button onClick={handleScrollTo} variant="secondary" fullWidth>
                {t.backToTop}
              </Button>
            </FooterSection>

            <Link
              href={homeLink}
              trackingObject={{
                action: 'Footer Click',
                label: '/sport',
                event: gtmEvents.ga,
                category: gtmCategories.navigation,
              }}
            >
              <Image src="/mybet_logo_dark.svg" width={101} height={32} />
            </Link>
          </>
        )}

        <FooterContent>
          <FooterSection as="nav" gutterBottom={2}>
            {links.map(({ id, href, onClick }) =>
              href ? (
                <Link
                  key={id}
                  href={href}
                  trackingObject={{
                    action: 'Footer Click',
                    label: href,
                    event: gtmEvents.ga,
                    category: gtmCategories.navigation,
                  }}
                >
                  <FooterLinkItem as="p" variant="body3">
                    {t[id]}
                  </FooterLinkItem>
                </Link>
              ) : (
                <div key={id} style={{ display: 'inline' }} onClick={onClick}>
                  <FooterLinkItem as="p" variant="body3">
                    {t[id]}
                  </FooterLinkItem>
                </div>
              )
            )}

            {infoLinks.map(({ id, label, href }) => (
              <Link
                key={id}
                href={href as string}
                trackingObject={{
                  action: 'Footer Click',
                  event: gtmEvents.ga,
                  category: gtmCategories.navigation,
                }}
              >
                <FooterLinkItem as="p" variant="body3">
                  {label}
                </FooterLinkItem>
              </Link>
            ))}
          </FooterSection>

          {LanguageSelectorProps && (
            <FooterSection gutterBottom={2}>
              <LanguageSelectorSection>
                <LanguageSelector {...LanguageSelectorProps} />
              </LanguageSelectorSection>
            </FooterSection>
          )}
          <FooterSection gutterBottom={3}>
            {paymentIcons.map((icon) => (
              <PaymentIconRoot key={icon}>
                <PaymentIcon
                  viewBox="0 0 79 48"
                  icon={icon}
                  height={48}
                  width={78}
                />
              </PaymentIconRoot>
            ))}
          </FooterSection>
          <FooterSection ref={thirdPartySectionRef}>
            {thirdPartyIcons.map(
              (icon) =>
                thirdPartySectionInView && (
                  <ThirdPartyIcon
                    key={icon}
                    icon={icon}
                    height={32}
                    width="auto"
                  />
                )
            )}
          </FooterSection>
        </FooterContent>

        <FooterEnd>
          <FooterSection ref={certificateSectionRef}>
            {certificateIcons.map(({ icon, href }) => {
              return href ? (
                <Link key={icon} href={href} style={{ cursor: 'pointer' }}>
                  {certificateSectionInView && (
                    <CertificateIcon
                      key={icon}
                      icon={icon}
                      height={32}
                      width="auto"
                    />
                  )}
                </Link>
              ) : (
                certificateSectionInView && (
                  <CertificateIcon
                    key={icon}
                    icon={icon}
                    height={32}
                    width="auto"
                  />
                )
              );
            })}
          </FooterSection>
          <Typography as="p" fontWeight={700} align="center" gutterTop={2}>
            {t.footerResposibleGaming}
          </Typography>
          <Typography as="p" align="center">
            {t.footerLegalText}
          </Typography>
        </FooterEnd>
      </ChildrenRoot>
    </Root>
  );
};

export default AppFooter;
